"use client";

import { useState, useId, useEffect, useRef } from "react";
import { gsap } from "gsap";

interface FloatingInputProps {
  label: string;
  id?: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

export function FloatingInput({
  label,
  id,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder = "",
  className = "",
}: FloatingInputProps) {
  const [focused, setFocused] = useState(false);
  const uid = useId();
  const inputId = id || uid;
  const isActive = focused || value.length > 0;

  const labelRef = useRef<HTMLLabelElement>(null);
  const underlineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (labelRef.current) {
      gsap.to(labelRef.current, {
        y: isActive ? -10 : 0,
        scale: isActive ? 0.78 : 1,
        color: isActive && focused ? "#db0004" : "#888",
        duration: 0.2,
        ease: "power2.out"
      });
    }
  }, [isActive, focused]);

  useEffect(() => {
    if (underlineRef.current) {
      gsap.to(underlineRef.current, {
        scaleX: focused ? 1 : 0,
        duration: 0.25,
        ease: "power2.out"
      });
    }
  }, [focused]);

  return (
    <div className={`relative ${className}`}>
      <input
        type={type}
        id={inputId}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={isActive ? placeholder : ""}
        className={`
          peer w-full pt-6 pb-2 px-4 bg-brand-offwhite border text-xs font-sans font-medium
          focus:outline-none transition-colors duration-200 placeholder:text-brand-subtle
          ${focused ? "border-brand-red" : "border-brand-border/60"}
        `}
      />
      <label
        ref={labelRef}
        htmlFor={inputId}
        className="absolute left-4 top-4 text-xs font-sans font-bold uppercase tracking-wider text-brand-muted origin-left pointer-events-none"
      >
        {label}{required && " *"}
      </label>
      {/* Focus underline */}
      <div
        ref={underlineRef}
        className="absolute bottom-0 left-0 h-[2px] w-full bg-brand-red scale-x-0 origin-left"
      />
    </div>
  );
}

interface FloatingTextareaProps {
  label: string;
  id?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  rows?: number;
  placeholder?: string;
  className?: string;
}

export function FloatingTextarea({
  label,
  id,
  value,
  onChange,
  required = false,
  rows = 4,
  placeholder = "",
  className = "",
}: FloatingTextareaProps) {
  const [focused, setFocused] = useState(false);
  const uid = useId();
  const inputId = id || uid;
  const isActive = focused || value.length > 0;

  const labelRef = useRef<HTMLLabelElement>(null);
  const underlineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (labelRef.current) {
      gsap.to(labelRef.current, {
        y: isActive ? -10 : 0,
        scale: isActive ? 0.78 : 1,
        color: isActive && focused ? "#db0004" : "#888",
        duration: 0.2,
        ease: "power2.out"
      });
    }
  }, [isActive, focused]);

  useEffect(() => {
    if (underlineRef.current) {
      gsap.to(underlineRef.current, {
        scaleX: focused ? 1 : 0,
        duration: 0.25,
        ease: "power2.out"
      });
    }
  }, [focused]);

  return (
    <div className={`relative ${className}`}>
      <textarea
        id={inputId}
        required={required}
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={isActive ? placeholder : ""}
        className={`
          w-full pt-7 pb-2 px-4 bg-brand-offwhite border text-xs font-sans font-medium
          focus:outline-none transition-colors duration-200 resize-none placeholder:text-brand-subtle
          ${focused ? "border-brand-red" : "border-brand-border/60"}
        `}
      />
      <label
        ref={labelRef}
        htmlFor={inputId}
        className="absolute left-4 top-4 text-xs font-sans font-bold uppercase tracking-wider text-brand-muted origin-left pointer-events-none"
      >
        {label}{required && " *"}
      </label>
      <div
        ref={underlineRef}
        className="absolute bottom-0 left-0 h-[2px] w-full bg-brand-red scale-x-0 origin-left"
      />
    </div>
  );
}
